import React, { useState } from "react";
import { useMediaQuery } from "@mui/material";
import { AppBar, Avatar, IconButton, Toolbar, Typography, Box, Skeleton, Badge } from "@mui/material";
import menuIcon from "../../assets/icons/menubar.png";
import bellIcon from "../../assets/icons/bell.png";
import Sidebar from "./Sidebar";
import { ProfilePopup } from "./AvatarOverlay";
import OverlayModal from "./OverlayModal";
import Profile from "../../pages/User/Profile";
import Settings from "../../pages/User/Settings";
import Support from "../../pages/User/Support";
import FAQ from "../../pages/User/FAQ";
import NotificationOverlay from "./NotificationOverlay";
import userImage from "../../assets/images/user.png";
import { useSidebar } from "../SidebarContext";
import { useUserProfile } from "../../hooks/useUserProfile";
import { auth } from "../../config/firebaseConfig";
import { getApiUrl, getApiHeaders } from "../../config/api";


const Header = ({ title = "UFind" }) => {
  const sidebarContext = useSidebar();
  const { isSidebarOpen = false, toggleSidebar = () => {} } = sidebarContext || {};
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isMobile = useMediaQuery("(max-width:600px)"); // Detects if it's a small screen
  
  // Get user data from custom hook (same source as Profile)
  const { photoURL, firstName, loading } = useUserProfile();
  // Function to close all popups
  const closeAllPopups = () => {
    setIsProfileOpen(false);
    setIsNotificationOpen(false);
  };

  const toggleProfilePopup = () => {
    closeAllPopups();
    setIsProfileOpen(!isProfileOpen);
  };

  // Overlay modal open handler
  const openOverlayModal = (contentType, options = {}) => {
    switch (contentType) {
      case "profile":
        setModalContent(<Profile />);
        break;
      case "settings":
        setModalContent(<Settings initialTab={options.tab} />);
        break;
      case "support":
        setModalContent(<Support />);
        break;
      case "faq":
        setModalContent(<FAQ />);
        break;
      default:
        setModalContent(null);
    }
    setModalOpen(true);
  };
  const closeOverlayModal = () => {
    setModalOpen(false);
    setModalContent(null);
  };

  const toggleNotificationOverlay = () => {
    closeAllPopups();
    setIsNotificationOpen(!isNotificationOpen);
  };

  // Fetch unread count
  React.useEffect(() => {
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(getApiUrl("api/notifications/unread-count"), {
          headers: getApiHeaders({ Authorization: `Bearer ${token}` })
        });
        if (!res.ok) return;
        const data = await res.json();
        if (mounted) setUnreadCount(data.unread_count || 0);
      } catch {}
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000); // refresh every 30s
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: "#fbffe8",
          top: 10,
          zIndex: 1100
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 2, sm: 4 },
            pl: { xs: 3, sm: 4 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {!isSidebarOpen && (
            <>
              <IconButton 
                edge="start" 
                color="inherit" 
                aria-label="menu" 
                sx={{ 
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  padding: { xs: '6px', sm: '8px' },
                  zIndex: 1300
                }} 
                onClick={toggleSidebar}
              >
                <img 
                  src={menuIcon} 
                  alt="Menu" 
                  style={{ 
                    width: isMobile ? 28 : 32, 
                    height: isMobile ? 28 : 32,
                    display: 'block'
                  }} 
                />
              </IconButton>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins, Helvetica",
                  fontWeight: 700,
                  color: "#516d4f",
                  fontSize: { xs: "20px", sm: "24px" },
                  letterSpacing: 0.5,
                  mr: 1,
                  display: 'flex',
                  alignItems: 'center',
                  height: { xs: '35px', sm: '40px' }
                }}
              >
                {title}
              </Typography>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {/* Notification Button */}
          <IconButton 
            color="inherit" 
            aria-label="notifications" 
            sx={{ 
              mr: { xs: 1, sm: 2 },
              padding: '8px',
              borderRadius: '12px',
              background: isNotificationOpen ? '#e1ecc8' : 'transparent',
              transition: 'background 0.2s, transform 0.2s',
              '&:hover': {
                backgroundColor: '#e1ecc8',
                transform: 'scale(1.05)',
              },
            }} 
            onClick={toggleNotificationOverlay}
          >
            <Badge color="error" badgeContent={unreadCount} overlap="circular">
              <img src={bellIcon} alt="Notifications" style={{ width: isMobile ? 30 : 35, height: isMobile ? 30 : 35 }} />
            </Badge>
          </IconButton>
          {/* Greeting and Avatar - only on desktop */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loading ? (
                <Skeleton 
                  variant="rounded" 
                  width={140} 
                  height={48} 
                  sx={{ borderRadius: '20px' }}
                />
              ) : (
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="profile"
                  onClick={toggleProfilePopup}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    borderRadius: '20px',
                    background: isProfileOpen ? '#e1ecc8' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Poppins, Helvetica",
                      fontWeight: 600,
                      color: "#516d4f",
                      fontSize: "18px",
                      mr: 1,
                    }}
                  >
                    {`Hi, ${firstName}!`}
                  </Typography>
                  <Avatar
                    alt="User Profile"
                    src={photoURL || userImage}
                    sx={{ width: 40, height: 40 }}
                  />
                </IconButton>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer Component */}
      <Sidebar 
        isOpen={isSidebarOpen} 
        toggleSidebar={toggleSidebar} 
        openOverlayModal={openOverlayModal}
        profilePhoto={photoURL}
        firstName={firstName}
        loading={loading}
      />

      {/* Profile Popup - only on desktop */}
      {!isMobile && isProfileOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            onClick={() => setIsProfileOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
              backgroundColor: "transparent",
            }}
          />
          {/* Popup */}
          <div
            style={{
              position: "absolute",
              top: "60px",
              right: "20px",
              zIndex: 1000,
            }}
          >
            <ProfilePopup openOverlayModal={openOverlayModal} />
          </div>
        </>
      )}

      {/* Overlay Modal for Profile/Settings/Support/FAQ */}
      <OverlayModal open={modalOpen} onClose={closeOverlayModal}>
        {modalContent}
      </OverlayModal>

      {/* Notification Overlay */}
      {isNotificationOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            onClick={() => setIsNotificationOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
              backgroundColor: "transparent",
            }}
          />
          {/* Popup */}
          <div
            style={{
              position: "absolute",
              top: "60px",
              right: "20px",
              zIndex: 1000,
            }}
          >
            <NotificationOverlay />
          </div>
        </>
      )}
    </>
  );
};

export default Header;
